import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, forwardRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { DsModule } from '@levelaccess/design-system';

import { IUserRole } from '../../../../shared/interfaces/user-role.interface';
import { $userRole } from '../../../../shared/constants/user-role';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { UserRoleService } from '../../services/user-role.service';
import { LoadErrorHandler } from '../../services/load-error-handler';
import { CommonPipesModule } from '../../shared/common-pipes.module';

const DEFAULT_LABEL: string = 'label_job_function';
const DEFAULT_PLACEHOLDER: string = 'select_job_function_placeholder';
const DEFAULT_EMPTY_VALUE: string = '';

@Component({
  selector: 'app-job-function-select',
  standalone: true,
  imports: [DsModule, CommonPipesModule],
  templateUrl: './job-function-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JobFunctionSelectComponent),
      multi: true,
    },
  ],
})
export class JobFunctionSelectComponent implements OnInit, ControlValueAccessor {
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public emptyValue: string | null;
  @Input() public required: boolean;
  @Input() public errorState: boolean;
  @Input() public errorMessage: string;

  public readonly $userRole: typeof $userRole;
  public roleOptions: IUserRole[];
  public disabled: boolean;
  public value: string | null;

  private onChange: (value: string | null) => void;
  private onTouched: () => void;

  constructor(
    private readonly userRoleService: UserRoleService,
    private readonly loadErrorHandler: LoadErrorHandler,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {
    this.label = DEFAULT_LABEL;
    this.placeholder = DEFAULT_PLACEHOLDER;
    this.emptyValue = DEFAULT_EMPTY_VALUE;
    this.required = true;
    this.errorState = false;
    this.errorMessage = '';
    this.$userRole = $userRole;
    this.roleOptions = [];
    this.disabled = false;
    this.value = null;
    this.onChange = (): void => {};
    this.onTouched = (): void => {};
  }

  public get normalizedSelection(): string {
    if (this.value === this.emptyValue || SharedCommonUtility.isNullish(this.value)) {
      return '';
    }

    const isValueAmongLoadedOptions: boolean = this.roleOptions.some(
      (role: IUserRole): boolean => role[$userRole._id] === this.value,
    );
    if (!isValueAmongLoadedOptions) {
      return '';
    }

    return this.value;
  }

  public ngOnInit(): void {
    this.loadRoleOptions();
  }

  public onRoleChange(event: Event): void {
    const selectedValue: string = (event.target as HTMLSelectElement).value;
    this.value = selectedValue === '' ? this.emptyValue : selectedValue;
    this.onTouched();
    this.onChange(this.value);
    this.changeDetectorRef.markForCheck();
  }

  public writeValue(value: string | null | undefined): void {
    this.value = value ?? this.emptyValue;
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetectorRef.markForCheck();
  }

  private loadRoleOptions(): void {
    this.userRoleService
      .getUserRoles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      // eslint-disable-next-line rxjs/no-ignored-subscription
      .subscribe({
        next: (roles: IUserRole[]): void => {
          this.roleOptions = roles;
          this.changeDetectorRef.markForCheck();
        },
        error: (error: HttpErrorResponse): void =>
          this.loadErrorHandler.handleError('error_loading_user_roles', error, JobFunctionSelectComponent.name),
      });
  }
}
